import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { departmentsApi, projectsApi } from '../api/client'
import { Link } from 'react-router-dom'
import { HiUserGroup, HiFolder, HiMail } from 'react-icons/hi'

export default function DepartmentPage() {
  const { departmentId } = useParams()
  const [department, setDepartment] = useState(null)
  const [members, setMembers] = useState([])
  const [projects, setProjects] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true)
        const [deptRes, membersRes, projectsRes] = await Promise.all([
          departmentsApi.get(departmentId),
          departmentsApi.getMembers(departmentId),
          projectsApi.list({ department_id: departmentId }),
        ])
        setDepartment(deptRes.data)
        setMembers(membersRes.data)
        setProjects(projectsRes.data)
      } catch (err) {
        console.error('Failed to fetch department:', err)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [departmentId])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-brand-500 border-t-transparent rounded-full"></div>
      </div>
    )
  }

  if (!department) {
    return (
      <div className="text-center py-16">
        <p className="text-surface-500 dark:text-surface-400">Department not found</p>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-surface-800 dark:text-surface-200">{department.name}</h1>
        {department.description && (
          <p className="text-surface-500 dark:text-surface-400 mt-1">{department.description}</p>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Members */}
        <div className="card">
          <div className="flex items-center gap-2 mb-4">
            <HiUserGroup className="w-5 h-5 text-brand-500" />
            <h2 className="font-semibold text-surface-800 dark:text-surface-200">Members ({members.length})</h2>
          </div>
          {members.length > 0 ? (
            <div className="space-y-3">
              {members.map(member => (
                <div 
                  key={member.id}
                  className="flex items-center gap-3 p-3 rounded-lg bg-surface-50 dark:bg-surface-800"
                >
                  <div className="w-10 h-10 bg-brand-100 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300 rounded-full flex items-center justify-center font-medium">
                    {member.display_name?.charAt(0) || 'U'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-surface-800 dark:text-surface-200 truncate">
                      {member.display_name}
                    </p>
                    <p className="text-sm text-surface-500 dark:text-surface-400 truncate flex items-center gap-1">
                      <HiMail className="w-3 h-3" />
                      {member.email}
                    </p>
                  </div>
                  <span className="text-xs text-surface-500 dark:text-surface-400 capitalize">
                    {member.department_role || 'member'}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-surface-500 dark:text-surface-400 py-8">No members yet</p>
          )}
        </div>

        {/* Projects */}
        <div className="card">
          <div className="flex items-center gap-2 mb-4">
            <HiFolder className="w-5 h-5 text-brand-500" />
            <h2 className="font-semibold text-surface-800 dark:text-surface-200">Projects ({projects.length})</h2>
          </div>
          {projects.length > 0 ? (
            <div className="space-y-2">
              {projects.map(project => (
                <Link
                  key={project.id}
                  to={`/projects/${project.id}`}
                  className="flex items-center gap-3 p-3 rounded-lg bg-surface-50 dark:bg-surface-800 hover:bg-surface-100 dark:hover:bg-surface-700 transition-colors"
                >
                  <span
                    className="w-3 h-3 rounded-full shrink-0"
                    style={{ backgroundColor: project.color || '#3b82f6' }}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-surface-800 dark:text-surface-200 truncate">{project.name}</p>
                    <p className="text-sm text-surface-500 dark:text-surface-400">
                      {project.task_count || 0} tasks
                    </p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded ${
                    project.status === 'active' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' :
                    project.status === 'completed' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' :
                    project.status === 'on_hold' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300' :
                    'bg-surface-100 text-surface-600 dark:bg-surface-700 dark:text-surface-400'
                  }`}>
                    {project.status}
                  </span>
                </Link>
              ))}
            </div>
          ) : (
            <p className="text-center text-surface-500 dark:text-surface-400 py-8">No projects yet</p>
          )}
        </div>
      </div>
    </div>
  )
}
